import React, { createContext, useContext } from 'react';

import { Preferences } from '@capacitor/preferences';

import {
  ONBOARDING_PREFERENCES_KEY,
  ONBOARDING_STATUS,
  OnboardingStatus
} from '@/utils/onboarding';
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

interface OnboardingContextProps {
  status: OnboardingStatus | undefined;
  onCompleteOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextProps | undefined>(
  undefined
);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const queryClient = useQueryClient();

  const { data: status }: UseQueryResult<OnboardingStatus, Error> = useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: async (): Promise<OnboardingStatus> => {
      try {
        const { value } = await Preferences.get({
          key: ONBOARDING_PREFERENCES_KEY
        });
        return (value as OnboardingStatus) || ONBOARDING_STATUS.PENDING;
      } catch {
        return ONBOARDING_STATUS.PENDING;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity
  });

  const completeOnboardingMutation = useMutation({
    mutationFn: async () => {
      await Preferences.set({
        key: ONBOARDING_PREFERENCES_KEY,
        value: ONBOARDING_STATUS.COMPLETED
      });
      return ONBOARDING_STATUS.COMPLETED;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['onboarding', 'status'], data);
    }
  });

  const onCompleteOnboarding = async () => {
    await completeOnboardingMutation.mutateAsync();
  };

  return (
    <OnboardingContext.Provider
      value={{
        status,
        onCompleteOnboarding
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextProps => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
